/**
 * Spusteni a stav auditu nad JEDNIM API (krok S3).
 *
 * Drive tenhle modul mel vlastni `VITE_AUDIT_API_URL` — audit bezel jako
 * samostatna sluzba vedle web-adminu (BLOK 9). Po sjednoceni je API jedno
 * (`lib/api.ts`, `@reos/api-client`), takze tady zustava jen domenovy postup;
 * zadna druha adresa, zadny druhy klient.
 *
 * Krok UXZ1: zalozeni zakazky uz audit NESPOUSTI (`createCaseAndQueueAudit` je
 * proto pryc). Zakazku zaklada `lib/case-create.ts`, beh spousti detail
 * zakazky pres `recheckCaseAudit` — jedine misto v UI, ktere beh zaradi.
 */

import type { ApiPaths } from '@reos/api-client';
import { t } from '@reos/i18n';
import { apiClient } from './api.js';
import { unwrap } from './api-error.js';
import { propertyAddressOf } from './case-create.js';

/** Krok PIL1: `propertyAddressOf` zije v `case-create.ts` (SSOT zalozeni zakazky). */
export { propertyAddressOf };

export type AuditRunDto =
  ApiPaths['/v1/audit-runs/{id}']['get']['responses']['200']['content']['application/json'];
export type AuditRunStatus = AuditRunDto['status'];

export async function getAuditRunStatus(runId: string): Promise<AuditRunDto> {
  return unwrap(apiClient.GET('/v1/audit-runs/{id}', { params: { path: { id: runId } } }));
}

/**
 * KROK QAFIX14 — UKAZKOVY (PREHRANY) BEH DEMO KANCELARE.
 *
 * Server v demu nic nepocita: ke zakazce zalozi zaznam opsany z ukazkove
 * fixtury a oznaci ho `acceptance.status = 'DEMO_SAMPLE'`
 * (`bindDemoRunToDeal` v `apps/api/src/routes/audit-runs.ts`). Rozpoznani je
 * TADY, jednou: cte ho spusteni provereni i nactený detail zakazky, a oba
 * musi kreslit tentyz pruh „Ukázkový report…".
 */
export const DEMO_SAMPLE_ACCEPTANCE_STATUS = 'DEMO_SAMPLE';

export function isDemoSampleRun(
  run: { readonly acceptance?: { readonly [key: string]: unknown } | null } | null | undefined,
): boolean {
  return (run?.acceptance ?? null)?.['status'] === DEMO_SAMPLE_ACCEPTANCE_STATUS;
}

/** Beh, ktery jeste bezi — UI u nej ceka (polling), ne hlasi hotovo. */
export function isRunPending(status: AuditRunStatus): boolean {
  return status === 'QUEUED' || status === 'RUNNING';
}

/**
 * Krok UX2 — „Prověřit znovu“: nový běh nad STEJNOU zakázkou přes stávající
 * `POST /v1/cases/:caseId/audit-runs` (krok S1). Server pod zakázkou založí
 * nový auditní případ a zařadí běh do fronty; `GET /v1/deals/:dealId/audit-runs`
 * je vrací od nejnovejšího, takže detail po obnovení sleduje nový běh.
 *
 * Záměr (intent) jde z katalogu, ne z klienta: běh musí jít do auditní stopy
 * se stejnou formulací jako v katalogu UI. Hromadné přeprověření se NEDĚLÁ
 * (zadání UX2) — stojí peníze, proto je to tady jen jeden běh na jeden klik
 * s potvrzovacím dialogem v `CaseFreshnessBar`.
 */
export interface RecheckAuditInput {
  /** Zakázka (`cases.id`) — z URL detailu. */
  readonly caseId: string;
  /**
   * ZJIŠTĚNÝ typ předmětu. Nepovinný: seznam zakázek ho nezná (je až v detailu)
   * a hádat ho nesmí — špatný typ zakázky je právě ta vada, kterou krok UX2
   * řeší. Když chybí, server ho převezme z posledního auditního případu.
   */
  readonly kind?: 'LAND' | 'HOUSE' | 'FLAT' | undefined;
  /**
   * Krok ZDARMA1: ROZSAH proverení. `zakladni` je BEZPLATNY beh jen nad
   * verejnymi strojovymi zdroji (ZR1), `plny` je dnesni placeny beh z kreditu
   * kancelare. Nepovinny: kdyz chybi, server dosadi `plny` (dnesni chovani),
   * takze volajici, ktery uroven neresi, se nemeni.
   */
  readonly reportLevel?: ReviewLevel | undefined;
  readonly addressQuery: string;
  readonly parcels: readonly string[];
  readonly cadastralArea: string | null;
  readonly landRegistryNumber: string | null;
  readonly unitNumber?: string | null | undefined;
}

export const RECHECK_INTENT = t('webAdmin.caseCockpit.freshness.recheckIntent', { locale: 'cs' });

/**
 * Krok ZDARMA1 — DVE UROVNE PROVĚŘENÍ. Ciselnik je SERVEROVY
 * (`ddReportLevelSchema` v @reos/duediligence); tady je jen jeho typ, aby UI
 * nemelo vlastni retezce. `zakladni` = zdarma, `plny` = z kreditu kancelare.
 */
export type ReviewLevel = NonNullable<
  NonNullable<ApiPaths['/v1/cases/{caseId}/audit-runs']['post']['requestBody']>['content']['application/json']['reportLevel']
>;

/** Vychozi volba v UI je BEZPLATNA uroven (rozhodnuti R.B. 14. 10. 2026). */
export const REVIEW_LEVEL_DEFAULT: ReviewLevel = 'zakladni';

/**
 * KROK NOC6 body 3 a 4 (nalezy N27, N28) — CENA, KREDIT A ZBYTEK STROPU.
 *
 * Jeden dotaz na kartu u zakazky (`GET /v1/cases/:caseId/review-options`):
 * cena kompletniho provereni z KATALOGU, zustatek knihy kreditu kancelare
 * a kolik bezplatnych behu v mesici jeste zbyva. Tri dotazy by na detailu
 * zakazky znamenaly tri polozky kvoty navic (poucení R5/O6a).
 */
export type ReviewOptionsDto =
  ApiPaths['/v1/cases/{caseId}/review-options']['get']['responses']['200']['content']['application/json'];

export async function getReviewOptions(caseId: string): Promise<ReviewOptionsDto> {
  return unwrap(
    apiClient.GET('/v1/cases/{caseId}/review-options', { params: { path: { caseId } } }),
  );
}

export async function recheckCaseAudit(input: RecheckAuditInput): Promise<AuditRunDto> {
  return unwrap(
    apiClient.POST('/v1/cases/{caseId}/audit-runs', {
      params: { path: { caseId: input.caseId } },
      body: {
        ...(input.kind !== undefined ? { kind: input.kind } : {}),
        ...(input.reportLevel === undefined ? {} : { reportLevel: input.reportLevel }),
        addressQuery: input.addressQuery,
        intent: RECHECK_INTENT,
        parcels: [...input.parcels],
        cadastralArea: input.cadastralArea,
        landRegistryNumber: input.landRegistryNumber,
        unitNumber: input.unitNumber ?? null,
        listingUrl: null,
      },
    }),
  );
}

/**
 * Krok UX2 — KDY BYLA ZAKAZKA NAPOSLEDY PROVERENA (davkove, pro seznam).
 *
 * Jeden request na celou stranku, ne jeden na radek — stejny duvod i stejny
 * tvar jako `listCaseCostsSummary` (`/v1/costs/summary?groupBy=case`): kroky R5
 * a O6a uz jednou odstranovaly N volani na stranku kvuli `RATE_LIMIT_MAX`.
 */
export type AuditRunSummaryItem =
  ApiPaths['/v1/audit-runs/summary']['get']['responses']['200']['content']['application/json']['items'][number];

/** Strop davky na serveru (`SUMMARY_MAX_DEALS` v apps/api/src/routes/audit-runs.ts). */
export const AUDIT_SUMMARY_MAX_DEALS = 200;

export async function listAuditRunSummary(
  dealIds: readonly string[],
): Promise<readonly AuditRunSummaryItem[]> {
  if (dealIds.length === 0) return [];
  const out: AuditRunSummaryItem[] = [];
  // Stranka seznamu muze byt delsi nez strop davky — radeji dva dotazy nez
  // jeden 400 a prazdny sloupec „Prověřeno".
  for (let from = 0; from < dealIds.length; from += AUDIT_SUMMARY_MAX_DEALS) {
    const chunk = dealIds.slice(from, from + AUDIT_SUMMARY_MAX_DEALS);
    const result = await unwrap(
      apiClient.GET('/v1/audit-runs/summary', { params: { query: { dealIds: chunk.join(',') } } }),
    );
    out.push(...result.items);
  }
  return out;
}

export type AuditRunBatchDto =
  ApiPaths['/v1/duediligence/batches/{id}']['get']['responses']['200']['content']['application/json'];
export type AuditRunBatchStatus = AuditRunBatchDto['status'];

/**
 * Krok O2: davkove spusteni auditu nad vybranymi nemovitostmi z `/nemovitosti`
 * (max 100 na davku, viz apps/api/src/routes/audit-run-batches.ts). Stejny
 * princip jako `recheckCaseAudit` - server je jediny zdroj pravdy,
 * tenhle modul jen vola API.
 */
export async function startAuditBatch(propertyIds: readonly string[]): Promise<AuditRunBatchDto> {
  return unwrap(apiClient.POST('/v1/duediligence/batches', { body: { propertyIds: [...propertyIds] } }));
}

export async function listAuditBatches(limit = 25): Promise<readonly AuditRunBatchDto[]> {
  const result = await unwrap(
    apiClient.GET('/v1/duediligence/batches', { params: { query: { limit } } }),
  );
  return result.items;
}

export async function getAuditBatch(batchId: string): Promise<AuditRunBatchDto> {
  return unwrap(apiClient.GET('/v1/duediligence/batches/{id}', { params: { path: { id: batchId } } }));
}

/** ADM1 bod 8: znovu zaradi do fronty polozky davky, ktere selhaly na behu. */
export async function retryFailedBatchItems(batchId: string): Promise<AuditRunBatchDto> {
  return unwrap(
    apiClient.POST('/v1/duediligence/batches/{id}/retry-failed', { params: { path: { id: batchId } } }),
  );
}
